/**
 * Performance Timing Utility
 * 
 * Lightweight performance measurement for mobile debugging.
 * Logs timing marks and intervals to console for analysis.
 */

export class PerformanceTiming {
  private static marks: Map<string, number> = new Map();
  private static enabled = true;

  /**
   * Mark a point in time
   */
  static mark(name: string) {
    if (!this.enabled) return;
    
    const time = Date.now();
    this.marks.set(name, time);
    console.log(`[PERF] ⏱️  ${name}: ${time}ms since epoch`);
  }

  /**
   * Measure duration between two marks
   */
  static measure(name: string, startMark: string, endMark?: string) {
    if (!this.enabled) return 0;

    const start = this.marks.get(startMark);
    const end = endMark ? this.marks.get(endMark) : Date.now();
    
    if (!start) {
      console.error(`[PERF] ❌ Start mark "${startMark}" not found`);
      return 0;
    }

    const duration = (end || Date.now()) - start;
    console.log(
      `[PERF] 📊 ${name}: ${duration}ms (${startMark} → ${endMark || 'now'})`
    );
    return duration;
  }

  /**
   * Get all marks (for debugging)
   */
  static getAll() {
    return Array.from(this.marks.entries()).map(([name, time]) => ({
      name,
      time,
    }));
  }

  /**
   * Clear all marks
   */
  static clear() {
    this.marks.clear();
  }

  /**
   * Enable/disable logging
   */
  static setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }
}

/**
 * Memory Monitor
 * 
 * Logs memory usage periodically (Chrome only, dev mode recommended)
 */
export function startMemoryMonitor(intervalMs = 5000) {
  const interval = setInterval(() => {
    if (performance.memory) {
      const used = (performance.memory.usedJSHeapSize / 1048576).toFixed(2);
      const total = (performance.memory.totalJSHeapSize / 1048576).toFixed(2);
      const limit = (performance.memory.jsHeapSizeLimit / 1048576).toFixed(2);
      
      console.log(
        `[MEMORY] 💾 ${used}MB / ${total}MB (limit: ${limit}MB)`
      );
    } else {
      console.log('[MEMORY] ⚠️  performance.memory not available (use Chrome)');
    }
  }, intervalMs);

  return () => clearInterval(interval);
}

